/**
 * Analytics API wrapper. Mirrors `apps/api/src/modules/analytics` routes.
 * All endpoints are GET-only aggregations; default `staleTime: 60_000`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";

import type { APIError } from "../http.js";
import { qk } from "../query-keys.js";

import { apiRequest } from "./_client.js";
import { useAuth } from "../../providers/auth-provider.js";
import { analyticsAccessKey } from "../analytics/access-key.js";

import type { analytics as AnalyticsContracts } from "@fynd-onshelf/contracts";

export type AnalyticsIssuesBreakdown = AnalyticsContracts.AnalyticsIssuesBreakdown;
export type AnalyticsTasksBreakdown = AnalyticsContracts.AnalyticsTasksBreakdown;
export type AnalyticsSessionsBreakdown = AnalyticsContracts.AnalyticsSessionsBreakdown;
export type AnalyticsProductsBreakdown = AnalyticsContracts.AnalyticsProductsBreakdown;
export type AnalyticsSummary = AnalyticsContracts.AnalyticsSummary;
export type AnalyticsIssuesResponse = AnalyticsContracts.AnalyticsIssuesResponse;
export type AnalyticsTasksResponse = AnalyticsContracts.AnalyticsTasksResponse;
export type AnalyticsSessionsResponse = AnalyticsContracts.AnalyticsSessionsResponse;
export type AnalyticsProductsResponse = AnalyticsContracts.AnalyticsProductsResponse;
export type TaskAnalyticsSummary = AnalyticsContracts.TaskAnalyticsSummary;
export type BrandShareEntry = AnalyticsContracts.BrandShareEntry;
export type CategoryShareEntry = AnalyticsContracts.CategoryShareEntry;
export type ShelfBrandBreakdown = AnalyticsContracts.ShelfBrandBreakdown;
export type RecognitionDiagnostics = AnalyticsContracts.RecognitionDiagnostics;
export type TaskAnalyticsResponse = AnalyticsContracts.TaskAnalyticsResponse;
export type SessionAnalyticsResponse = AnalyticsContracts.SessionAnalyticsResponse;
export type TrendBucket = AnalyticsContracts.TrendBucket;
export type AnalyticsTrendPoint = AnalyticsContracts.AnalyticsTrendPoint;
export type AnalyticsTrendResponse = AnalyticsContracts.AnalyticsTrendResponse;
export type AnalyticsRollupResponse = AnalyticsContracts.AnalyticsRollupResponse;
export type AnalyticsTrendQuery = Partial<AnalyticsContracts.AnalyticsTrendQuery>;
export type AnalyticsProductsByCategory = AnalyticsProductsResponse["byCategory"][number];
export interface AnalyticsStoreQuery { storeId?: string }

export const analyticsApi = {
  summary: (query: AnalyticsStoreQuery = {}, signal?: AbortSignal): Promise<AnalyticsSummary> =>
    apiRequest({ method: "GET", url: "/api/v2/analytics/summary", params: { storeId: query.storeId }, signal }),

  trends: (query: AnalyticsTrendQuery, signal?: AbortSignal): Promise<AnalyticsTrendResponse> =>
    apiRequest({
      method: "GET",
      url: "/api/v2/analytics/trends",
      params: query as Record<string, string | undefined>,
      signal,
    }),

  rebuildRollup: (body: { from?: string; to?: string }): Promise<AnalyticsRollupResponse> =>
    apiRequest({ method: "POST", url: "/api/v2/analytics/rollup", data: body }),

  taskReport: (taskId: string, signal?: AbortSignal): Promise<TaskAnalyticsResponse> =>
    apiRequest({
      method: "GET",
      url: `/api/v2/analytics/capture-tasks/${encodeURIComponent(taskId)}/report`,
      signal,
    }),

  sessionReport: (
    sessionId: string,
    signal?: AbortSignal,
  ): Promise<SessionAnalyticsResponse> =>
    apiRequest({
      method: "GET",
      url: `/api/v2/analytics/capture-sessions/${encodeURIComponent(sessionId)}/report`,
      signal,
    }),

  issues: (query: AnalyticsStoreQuery = {}, signal?: AbortSignal): Promise<AnalyticsIssuesResponse> =>
    apiRequest({ method: "GET", url: "/api/v2/analytics/issues", params: { storeId: query.storeId }, signal }),

  tasks: (query: AnalyticsStoreQuery = {}, signal?: AbortSignal): Promise<AnalyticsTasksResponse> =>
    apiRequest({ method: "GET", url: "/api/v2/analytics/tasks", params: { storeId: query.storeId }, signal }),

  sessions: (query: AnalyticsStoreQuery = {}, signal?: AbortSignal): Promise<AnalyticsSessionsResponse> =>
    apiRequest({ method: "GET", url: "/api/v2/analytics/sessions", params: { storeId: query.storeId }, signal }),

  products: (signal?: AbortSignal): Promise<AnalyticsProductsResponse> =>
    apiRequest({ method: "GET", url: "/api/v2/analytics/products", signal }),
};

export function useAnalyticsSummaryQuery(
  query: AnalyticsStoreQuery = {},
  options?: Omit<UseQueryOptions<AnalyticsSummary, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<AnalyticsSummary, APIError>({
    queryKey: [...qk.analytics.summary, analyticsAccessKey(useAuth().user), query],
    queryFn: ({ signal }) => analyticsApi.summary(query, signal),
    retry: false,
    staleTime: 60_000,
    ...options,
  });
}

export function useAnalyticsIssuesQuery(
  query: AnalyticsStoreQuery = {},
  options?: Omit<UseQueryOptions<AnalyticsIssuesResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<AnalyticsIssuesResponse, APIError>({
    queryKey: [...qk.analytics.issues, analyticsAccessKey(useAuth().user), query],
    queryFn: ({ signal }) => analyticsApi.issues(query, signal),
    retry: false,
    staleTime: 60_000,
    ...options,
  });
}

export function useAnalyticsTasksQuery(
  query: AnalyticsStoreQuery = {},
  options?: Omit<UseQueryOptions<AnalyticsTasksResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<AnalyticsTasksResponse, APIError>({
    queryKey: [...qk.analytics.tasks, analyticsAccessKey(useAuth().user), query],
    queryFn: ({ signal }) => analyticsApi.tasks(query, signal),
    retry: false,
    staleTime: 60_000,
    ...options,
  });
}

export function useAnalyticsSessionsQuery(
  query: AnalyticsStoreQuery = {},
  options?: Omit<UseQueryOptions<AnalyticsSessionsResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<AnalyticsSessionsResponse, APIError>({
    queryKey: [...qk.analytics.sessions, analyticsAccessKey(useAuth().user), query],
    queryFn: ({ signal }) => analyticsApi.sessions(query, signal),
    retry: false,
    staleTime: 60_000,
    ...options,
  });
}

export function useAnalyticsProductsQuery(
  options?: Omit<UseQueryOptions<AnalyticsProductsResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<AnalyticsProductsResponse, APIError>({
    queryKey: [...qk.analytics.products, analyticsAccessKey(useAuth().user)],
    queryFn: ({ signal }) => analyticsApi.products(signal),
    retry: false,
    staleTime: 60_000,
    ...options,
  });
}

export function useTaskAnalyticsQuery(
  taskId: string,
  options?: Omit<UseQueryOptions<TaskAnalyticsResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<TaskAnalyticsResponse, APIError>({
    queryKey: [...qk.analytics.taskReport(taskId), analyticsAccessKey(useAuth().user)],
    queryFn: ({ signal }) => analyticsApi.taskReport(taskId, signal),
    retry: false,
    enabled: taskId !== "",
    staleTime: 30_000,
    ...options,
  });
}

export function useSessionAnalyticsQuery(
  sessionId: string,
  options?: Omit<UseQueryOptions<SessionAnalyticsResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<SessionAnalyticsResponse, APIError>({
    queryKey: [...qk.analytics.sessionReport(sessionId), analyticsAccessKey(useAuth().user)],
    queryFn: ({ signal }) => analyticsApi.sessionReport(sessionId, signal),
    retry: false,
    enabled: sessionId !== "",
    staleTime: 30_000,
    ...options,
  });
}

export function useAnalyticsTrendsQuery(
  query: AnalyticsTrendQuery,
  options?: Omit<UseQueryOptions<AnalyticsTrendResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<AnalyticsTrendResponse, APIError>({
    queryKey: [...qk.analytics.trends(query as Record<string, unknown>), analyticsAccessKey(useAuth().user)],
    queryFn: ({ signal }) => analyticsApi.trends(query, signal),
    retry: false,
    staleTime: 60_000,
    ...options,
  });
}

/** Rebuild the store-day rollup (backfill). Invalidates trend queries so the
 *  chart refreshes once the rows are materialized. */
export function useRebuildRollupMutation(
  options?: Omit<
    UseMutationOptions<AnalyticsRollupResponse, APIError, { from?: string; to?: string }>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AnalyticsRollupResponse, APIError, { from?: string; to?: string }>({
    ...options,
    mutationFn: (body) => analyticsApi.rebuildRollup(body),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      void qc.invalidateQueries({ queryKey: ["analytics", "trends"] });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}
